"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import type { ReactNode } from "react";

const PUBLIC_ROUTES = new Set(["/login", "/", "/docs"]);

export default function AuthGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const isPublicRoute = pathname ? PUBLIC_ROUTES.has(pathname) : false;

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublicRoute) {
      const callbackUrl = typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : pathname || "/";
      router.replace(`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`);
    }
  }, [isAuthenticated, isLoading, isPublicRoute, pathname, router]);

  if (isPublicRoute) return <>{children}</>;

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm px-8 py-6 text-center">
          <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-primary-200 border-t-primary-600 animate-spin" />
          <p className="text-sm font-medium text-slate-700">Checking your session</p>
          <p className="text-xs text-slate-400 mt-1">Redirecting to sign in if needed...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
